import React, { useState } from 'react';
import Seo, { faqLd, serviceLd } from '../../components/Seo';
import Reveal from '../../components/Reveal';
import {
  CheckItem, Faq, Icon, IconCards, Intro, ProductHero, QuoteSection, ScreenTour, SectionTitle, Steps,
} from '../../components/product/ProductKit';
import { SOFTPHONE as D } from '../../data/softphone';
import { useSiteContent, withProductContent } from '../../content/siteContent';

/** Feature groups as tabs: a list on the left, the selected group's details on the right. */
const FeatureTabs = () => {
  const [active, setActive] = useState(0);
  const f = D.features[active];
  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
      <div role="tablist" aria-label="Softphone features" className="flex gap-2 overflow-x-auto pb-2 lg:col-span-4 lg:flex-col lg:overflow-visible lg:pb-0">
        {D.features.map((g, i) => (
          <button
            key={g.id}
            type="button"
            role="tab"
            id={`tab-${g.id}`}
            aria-selected={active === i}
            aria-controls={`panel-${g.id}`}
            onClick={() => setActive(i)}
            className={`flex shrink-0 items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-semibold transition-colors ${active === i ? 'bg-[#0a1f2e] text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
          >
            <Icon name={g.icon} className={`h-4 w-4 ${active === i ? 'text-[#c8ff00]' : 'text-gray-500'}`} />
            {g.title}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`panel-${f.id}`}
        aria-labelledby={`tab-${f.id}`}
        key={f.id}
        className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10 lg:col-span-8 lg:self-start"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0a1f2e] text-[#c8ff00]"><Icon name={f.icon} className="h-6 w-6" /></span>
        <h3 className="mt-6 font-display text-3xl font-semibold tracking-tight">{f.title}</h3>
        <p className="mt-2 text-lg text-gray-600">{f.body}</p>
        <ul className="mt-8 grid grid-cols-1 gap-4 text-gray-700 sm:grid-cols-2">
          {f.points.map((p) => <CheckItem key={p}>{p}</CheckItem>)}
        </ul>
      </div>
    </div>
  );
};

const Softphone = () => {
  const P = withProductContent(useSiteContent('softphone'), D, { trust: false });
  return (
  <div className="min-h-screen bg-white text-[#0a1f2e]">
    <Seo
        title="Softphone App for Desktop, Mobile & Web"
        description="The VOIP2 softphone: calls, video meetings and team chat on Windows, macOS, iOS, Android and the browser. Included with every Cloud PBX plan."
        path="/products/softphone"
        breadcrumbs={[{ name: 'Softphone', path: '/products/softphone' }]}
        jsonLd={[serviceLd({ name: 'Softphone', description: "The VOIP2 softphone: calls, video meetings and team chat on Windows, macOS, iOS, Android and the browser. Included with every Cloud PBX plan.", path: '/products/softphone' }), faqLd(D.faqs)]}
      />

    <ProductHero
      {...P.hero}
      primary={{ label: 'Book a demo', href: '#quote' }}
      secondary={{ label: 'See the features', href: '#features' }}
      media={(
        <>
          <div className="absolute inset-x-8 bottom-6 top-16 -z-10 rounded-[3rem] bg-[radial-gradient(ellipse_at_center,rgba(200,255,0,0.3),transparent_65%)]" />
          <img src={D.hero.image} alt="The VOIP2 softphone on a laptop and a mobile phone, with a headset" className="w-full" />
        </>
      )}
    />

    {/* Feature chips */}
    <section className="border-y border-gray-100 px-4 py-8 sm:px-6 lg:px-8">
      <Reveal as="ul" className="mx-auto flex max-w-7xl flex-wrap justify-center gap-2">
        {D.chips.map((c) => (
          <li key={c} className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700">{c}</li>
        ))}
      </Reveal>
    </section>

    <Intro {...P.intro} />

    <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title="One app, every way your team talks" sub="Calls, chat, meetings and mobile in the same app and the same login. Click a screen for a closer look." />
        <ScreenTour screens={D.screens} />
      </div>
    </section>

    <section id="features" className="scroll-mt-20 px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title="Softphone features" sub="Pick a topic to see what is included." />
        <FeatureTabs />
      </div>
    </section>

    <section className="bg-[#0a1f2e] px-4 py-24 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title="Use any device, anywhere" sub="Start a call on your desktop and finish it on your mobile. Your number and settings follow you." light />
        <IconCards items={D.devices} columns={4} dark />
      </div>
    </section>

    <section className="px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title="Calling in three steps" />
        <Steps steps={D.steps} />
      </div>
    </section>

    <Faq faqs={D.faqs} />

    <QuoteSection
      title="See the softphone in action"
      sub="Book a live demo and we will show you calls, chat and meetings on your own devices."
      service="Softphone"
      contextLabel="How many users?"
      contextOptions={['1–5 users', '6–15 users', '16–50 users', '51–100 users', '100+ users']}
      related={D.related}
    />
  </div>
  );
};

export default Softphone;
